/**
 * Aplica la apariencia que el administrador guardó.
 *
 * Se inyecta como una HOJA `:root{…}` y no como estilo en línea sobre
 * `documentElement`. La diferencia importa: un estilo en línea gana a todo, y
 * se llevaría por delante el modo oscuro. Una regla `:root` tiene menos
 * especificidad que los bloques de oscuro de `styles.css`
 * —`:root:not([data-theme="light"])` y `:root[data-theme="dark"]`—, así que
 * pierde contra ellos y gana contra la paleta clara, que es exactamente lo que
 * se quiere: el ajuste es de la paleta CLARA.
 *
 * Los valores se vuelven a comprobar aquí aunque el servidor ya los valide.
 * Esto escribe texto DENTRO de una hoja de estilos: un valor que no fuera un
 * color podría cerrar la declaración y abrir otra cosa. La comprobación del
 * servidor es la que manda; esta es la que impide que un fallo suyo se
 * convierta en inyección de CSS.
 */

import type { BrandAppearance } from "../api/types";

const COLOR = /^#[0-9a-f]{6}$/i;

/** Mezcla hacia el negro. El estado presionado de un color no puede ser fijo. */
function oscurecer(hex: string, factor: number): string {
  const canal = (i: number) =>
    Math.round(parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) * (1 - factor))
      .toString(16)
      .padStart(2, "0");
  return `#${canal(0)}${canal(1)}${canal(2)}`;
}

/**
 * Takes `BrandAppearance` and not `Appearance` because the gate calls this too,
 * with the public payload, which is the narrower of the two. `Appearance` still
 * satisfies it — it only adds fields.
 */
export function aplicarApariencia(apariencia: BrandAppearance | undefined): void {
  if (!apariencia) return;
  const { table_header: cabecera, table_header_ink: tinta } = apariencia;
  const { table_zebra: cebra, structure: estructura, page_bg: pagina } = apariencia;
  if (![cabecera, tinta, cebra, estructura, pagina].every((v) => COLOR.test(v ?? ""))) {
    // Sin apariencia válida se deja la hoja de estilos como está, que trae los
    // colores de fábrica. Un color torcido no puede dejar a nadie sin pantalla.
    return;
  }

  const id = "apariencia";
  let hoja = document.getElementById(id) as HTMLStyleElement | null;
  if (!hoja) {
    hoja = document.createElement("style");
    hoja.id = id;
    document.head.append(hoja);
  }
  hoja.textContent = [
    ":root{",
    `--page:${pagina};`,
    `--th-bg:${cabecera};`,
    `--th-ink:${tinta};`,
    `--fila-hover:${cebra};`,
    `--fuerte:${estructura};`,
    `--fuerte-2:${oscurecer(estructura, 0.3)};`,
    "}",
  ].join("");
}


/* ---------------------------------------------------------------------------
 * Contraste, para avisar ANTES de guardar.
 *
 * El servidor sigue siendo la autoridad —valida lo mismo y rechaza con 400—,
 * pero enterarse al pulsar «Guardar», y encima sin saber cuál de los cuatro
 * colores falla, convierte una decisión visual en un juego de adivinanzas. Esto
 * marca el campo culpable mientras se elige.
 *
 * Es la misma aritmética de WCAG que `apariencia.py`. Está duplicada a
 * sabiendas: la alternativa era un viaje al servidor por cada movimiento del
 * selector de color.
 * ------------------------------------------------------------------------- */

/** Tinta del cuerpo. No es configurable, así que sirve de referencia fija. */
export const TINTA_CUERPO = "#374151";

function canal(v: number): number {
  const c = v / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function luminancia(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

export function contraste(uno: string, otro: string): number {
  const a = luminancia(uno);
  const b = luminancia(otro);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** El aviso de un color, o `undefined` si es publicable. */
export function reparo(campo: string, valor: string): string | undefined {
  if (!COLOR.test(valor)) return "No es un color válido.";
  switch (campo) {
    case "table_header":
      // La tinta de la cabecera se deduce; solo falla si ninguna de las dos
      // opciones contrasta, que es una franja estrecha de grises medios.
      return Math.max(contraste(valor, "#ffffff"), contraste(valor, TINTA_CUERPO)) < 4.5
        ? "Ni la tinta blanca ni la oscura contrastan sobre este tono."
        : undefined;
    case "structure":
      return contraste(valor, "#ffffff") < 4.5
        ? "Lleva texto blanco encima: tiene que ser bastante más oscuro."
        : undefined;
    case "page_bg":
    case "table_zebra":
      return contraste(valor, TINTA_CUERPO) < 7
        ? "Es fondo de texto: con este tono el cuerpo deja de leerse."
        : undefined;
    default:
      return undefined;
  }
}
